import React, { useState, useEffect } from 'react';
import { FileCheck, Shield, Users, ChevronDown, LayoutGrid, ExternalLink } from 'lucide-react';
import classes from './ModuleSelector.module.css';
import { launchPolicyBuilder } from '../../utils/policyHandoff';
import { useToast } from '../../contexts/ToastContext';

const ModuleSelector = ({
    isCollapsed = false,
    className = '',
    variant = 'sidebar',
    user,
    organizationId
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { showToast } = useToast();

    // Close dropdown on click outside
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = () => setIsOpen(false);
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, [isOpen]);

    const modules = [
        {
            id: 'compliance',
            name: 'Compliance',
            desc: 'Continuous Compliance',
            icon: Shield,
            active: true
        },
        {
            id: 'policy',
            name: 'Policy',
            desc: 'Management & Governance',
            icon: FileCheck,
            launchable: true
        },
        {
            id: 'monitor',
            name: 'Monitor',
            desc: 'Continuous Monitoring',
            icon: Users,
            badge: 'Soon'
        }
    ];

    const currentModule = modules.find(m => m.active) || modules[0];

    return (
        <div
            className={`${classes.moduleSelectorContainer} ${isCollapsed ? classes.isCollapsed : ''} ${classes[variant]} ${className}`}
            onClick={(e) => e.stopPropagation()}
        >
            <button
                className={`${classes.moduleTrigger} ${isOpen ? classes.moduleTriggerOpen : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title={isCollapsed ? `Current: ${currentModule.name}` : undefined}
            >
                <div className={classes.moduleIconWrapper}>
                    {variant === 'header' ? (
                        <LayoutGrid size={15} />
                    ) : (
                        <currentModule.icon size={isCollapsed ? 18 : 16} />
                    )}
                </div>
                {(!isCollapsed || variant === 'header') && (
                    <>
                        <div className={classes.moduleTriggerContent}>
                            <div className={classes.moduleValueWrapper}>
                                <span className={classes.moduleTriggerValue}>{currentModule.name}</span>
                                {variant === 'header' && <div className={classes.activePulse} />}
                            </div>
                            <span className={classes.moduleTriggerLabel}>
                                {variant === 'header' ? 'APP LAUNCHER' : 'WORKSPACE'}
                            </span>
                        </div>
                        <ChevronDown size={14} className={classes.moduleTriggerChevron} />
                    </>
                )}
            </button>

            {isOpen && (
                <div className={`${classes.moduleDropdown} ${variant === 'header' ? classes.dropdownHeader : ''}`}>
                    <div className={classes.dropdownTitle}>SWITCH WORKSPACE</div>
                    {modules.map((mod) => {
                        const Icon = mod.icon;
                        const isLaunchable = !!mod.launchable;
                        const isDisabled = !mod.active && !isLaunchable;

                        const content = (
                            <>
                                <div className={classes.moduleOptionIcon}>
                                    <Icon size={18} />
                                </div>
                                <div className={classes.moduleOptionInfo}>
                                    <div className={classes.moduleOptionHeader}>
                                        <span className={classes.moduleOptionName}>{mod.name}</span>
                                        {isLaunchable && <ExternalLink size={10} className={classes.launchIcon} />}
                                    </div>
                                    <span className={classes.moduleOptionDesc}>{mod.desc}</span>
                                </div>
                                {mod.badge && <span className={classes.moduleOptionBadge}>{mod.badge}</span>}
                            </>
                        );

                        const handleClick = async () => {
                            if (isLaunchable) {
                                try {
                                    await launchPolicyBuilder(user, organizationId, {
                                        onPopupBlocked: () => showToast('New tab was blocked. Opening Policy app in this tab instead.', 'info')
                                    });
                                    setIsOpen(false);
                                } catch (err) {
                                    // Error handling handled by utility or page
                                }
                                return;
                            }
                            setIsOpen(false);
                        };

                        return (
                            <div
                                key={mod.id}
                                className={`
                                    ${classes.moduleOption} 
                                    ${mod.active ? classes.moduleOptionActive : ''} 
                                    ${isDisabled ? classes.moduleOptionDisabled : ''}
                                    ${isLaunchable ? classes.moduleOptionLaunchable : ''}
                                `}
                                onClick={handleClick}
                            >
                                {content}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ModuleSelector;
